import { useCallback, useEffect, useMemo, useState } from "react";
import {
  apiHostingModeFromUi,
  fetchDocsSiteSettings,
  uiHostingModeFromApi,
  updateDocsSiteSettings,
  verifyDocsDomain,
} from "@/api/docsSiteApi";
import { Banner } from "@/pages/settings/settingsUi";
import { DOMAIN_STATUS, publicPageUrl } from "../docsSiteConstants";
import {
  ChecklistItem,
  DnsRecordCard,
  SectionCard,
  StatusBadge,
  Toggle,
  UrlDisplay,
} from "./docsSiteUi";

const HOSTING_MODES = [
  {
    id: "sentrodesk",
    label: "SentroDesk-hosted",
    desc: "Instant setup on SentroDesk docs — no DNS changes required.",
    icon: "ri-cloud-line",
    badge: "Recommended",
  },
  {
    id: "custom",
    label: "Custom domain",
    desc: "Serve docs from your own hostname, e.g. docs.yourcompany.com.",
    icon: "ri-links-line",
    badge: "White-label",
  },
];

function HostingModeCard({ mode, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative w-full rounded-2xl border p-5 text-left transition-all duration-200 ${
        active
          ? "border-teal-400/80 bg-gradient-to-br from-teal-50/90 to-white shadow-md shadow-teal-900/[0.06] ring-2 ring-teal-200/80 dark:border-teal-600/60 dark:from-teal-950/30 dark:to-slate-900 dark:ring-teal-800/40"
          : "border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-900/60 dark:hover:border-slate-600"
      }`}
    >
      <span
        className={`absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors ${
          active ? "border-teal-600 bg-teal-600" : "border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
        }`}
      >
        {active && <i className="ri-check-line text-[10px] text-white" aria-hidden />}
      </span>

      <div className="flex items-start gap-4 pr-8">
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
            active
              ? "bg-teal-600 text-white shadow-lg shadow-teal-900/20"
              : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          <i className={`${mode.icon} text-xl`} aria-hidden />
        </span>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-slate-900 dark:text-slate-100">{mode.label}</p>
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              {mode.badge}
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{mode.desc}</p>
        </div>
      </div>
    </button>
  );
}

function QuickTips() {
  return (
    <SectionCard icon="ri-lightbulb-line" title="Quick tips">
      <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
        <li className="flex gap-2">
          <i className="ri-check-double-line shrink-0 text-teal-600" aria-hidden />
          <span>Use your workspace slug in URLs until a custom domain is connected.</span>
        </li>
        <li className="flex gap-2">
          <i className="ri-check-double-line shrink-0 text-teal-600" aria-hidden />
          <span>Publish pages from the Pages tab before sharing links externally.</span>
        </li>
        <li className="flex gap-2">
          <i className="ri-check-double-line shrink-0 text-teal-600" aria-hidden />
          <span>All custom domains CNAME to the same SentroDesk target.</span>
        </li>
      </ul>
    </SectionCard>
  );
}

export default function DocsHostingPanel({
  workspaceSlug,
  workspaceName,
  published,
  onPublishedChange,
  settings,
  onSettingsChange,
}) {
  const slug = workspaceSlug || "your-workspace";
  const [hostingMode, setHostingMode] = useState("sentrodesk");
  const [customDomain, setCustomDomain] = useState("");
  const [domainStatus, setDomainStatus] = useState(DOMAIN_STATUS.NOT_CONFIGURED);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [cnameTarget, setCnameTarget] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!settings) return;
    setHostingMode(uiHostingModeFromApi(settings.hostingMode));
    setCustomDomain(settings.customDomain || "");
    setDomainStatus(settings.domainStatus || DOMAIN_STATUS.NOT_CONFIGURED);
    setDnsRecords(settings.dnsRecords || []);
    setCnameTarget(settings.cnameTarget || "");
    onPublishedChange?.(Boolean(settings.isPublished));
  }, [settings, onPublishedChange]);

  const sentrodeskUrl = settings?.sentrodeskBaseUrl || publicPageUrl(settings);
  const customUrl = customDomain.trim() ? `https://${customDomain.trim().toLowerCase()}` : null;

  const persistSettings = useCallback(
    async (patch) => {
      setSaving(true);
      setBanner(null);
      try {
        const updated = await updateDocsSiteSettings(patch);
        onSettingsChange?.(updated);
        return updated;
      } catch (e) {
        setBanner({ type: "error", text: e.message || "Could not save settings" });
        throw e;
      } finally {
        setSaving(false);
      }
    },
    [onSettingsChange],
  );

  async function handleHostingModeChange(modeId) {
    setHostingMode(modeId);
    try {
      await persistSettings({ hostingMode: apiHostingModeFromUi(modeId) });
    } catch {
      setHostingMode(uiHostingModeFromApi(settings?.hostingMode));
    }
  }

  async function handlePublishToggle(next) {
    onPublishedChange?.(next);
    try {
      await persistSettings({ isPublished: next });
    } catch {
      onPublishedChange?.(!next);
    }
  }

  async function handleCustomDomainBlur() {
    if (hostingMode !== "custom") return;
    const domain = customDomain.trim().toLowerCase() || null;
    if (domain === (settings?.customDomain || null)) return;
    try {
      await persistSettings({ customDomain: domain, hostingMode: "custom_domain" });
    } catch {
      setCustomDomain(settings?.customDomain || "");
    }
  }

  async function handleVerifyDomain() {
    if (!customDomain.trim()) {
      setBanner({ type: "error", text: "Enter a custom domain before verifying." });
      return;
    }
    setVerifying(true);
    setBanner(null);
    try {
      await persistSettings({ customDomain: customDomain.trim().toLowerCase(), hostingMode: "custom_domain" });
      const updated = await verifyDocsDomain();
      onSettingsChange?.(updated);
      setBanner({ type: "ok", text: "Domain verification completed." });
    } catch (e) {
      setBanner({ type: "error", text: e.message || "Domain verification failed" });
    } finally {
      setVerifying(false);
    }
  }

  const checklistDone = useMemo(
    () => ({
      cname: domainStatus === DOMAIN_STATUS.VERIFIED || domainStatus === DOMAIN_STATUS.ACTIVE,
      txt: domainStatus === DOMAIN_STATUS.VERIFIED || domainStatus === DOMAIN_STATUS.ACTIVE,
      verify: domainStatus === DOMAIN_STATUS.ACTIVE,
      publish: published,
    }),
    [domainStatus, published],
  );

  return (
    <div className="space-y-6">
      <QuickTips />

      {banner && (
        <Banner type={banner.type === "ok" ? "ok" : undefined} text={banner.text} onDismiss={() => setBanner(null)} />
      )}

      <div className={`grid gap-8 ${hostingMode === "custom" ? "xl:grid-cols-[minmax(0,1fr)_320px]" : ""}`}>
        <div className="min-w-0 space-y-6">
          <SectionCard
            icon="ri-toggle-line"
            title="Publishing"
            description={`Control public visibility for ${workspaceName}. When enabled, published pages become accessible at your docs URL.`}
            action={
              <div className="inline-flex items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900/50">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  {published ? "Published" : "Unpublished"}
                </span>
                <Toggle
                  on={published}
                  onToggle={handlePublishToggle}
                  disabled={saving}
                  ariaLabel={published ? "Unpublish docs site" : "Publish docs site"}
                />
              </div>
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {HOSTING_MODES.map((mode) => (
                <HostingModeCard
                  key={mode.id}
                  mode={mode}
                  active={hostingMode === mode.id}
                  onSelect={() => handleHostingModeChange(mode.id)}
                />
              ))}
            </div>
          </SectionCard>

          {hostingMode === "sentrodesk" && (
            <SectionCard
              icon="ri-link"
              title="SentroDesk docs URLs"
              description="Share these links with your customers. Paths are scoped to your workspace slug."
            >
              <div className="space-y-4">
                <UrlDisplay label="Site home" url={sentrodeskUrl} hint="Public root" />
                <UrlDisplay label="Example article" url={publicPageUrl(settings, "getting-started")} hint="Sample path" />
              </div>
            </SectionCard>
          )}

          {hostingMode === "custom" && (
            <>
              <SectionCard
                icon="ri-earth-line"
                title="Custom domain"
                description="Connect a hostname you control. We recommend a subdomain such as docs.yourcompany.com."
                action={<StatusBadge status={domainStatus} />}
              >
                <div className="max-w-xl">
                  <label htmlFor="custom-docs-domain" className="mb-1.5 block text-sm font-semibold text-slate-800 dark:text-slate-200">
                    Domain name
                  </label>
                  <input
                    id="custom-docs-domain"
                    type="text"
                    value={customDomain}
                    onChange={(e) => {
                      setCustomDomain(e.target.value);
                      setDomainStatus(DOMAIN_STATUS.NOT_CONFIGURED);
                    }}
                    onBlur={handleCustomDomainBlur}
                    placeholder="docs.yourcompany.com"
                    className="ui-field flex-1 w-full"
                    autoComplete="off"
                  />
                </div>

                {customUrl && (
                  <div className="mt-5">
                    <UrlDisplay
                      label="Branded URL"
                      url={customUrl}
                      hint={domainStatus === DOMAIN_STATUS.ACTIVE ? "Active" : "Pending verification"}
                    />
                  </div>
                )}
              </SectionCard>

              <SectionCard
                icon="ri-server-line"
                title="DNS configuration"
                description={
                  <>
                    Point your domain to{" "}
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-800">
                      {cnameTarget || "docs.sentrodesk.com"}
                    </code>
                    . Add the records below at your DNS provider.
                  </>
                }
              >
                {!customDomain.trim() ? (
                  <p className="text-sm text-slate-500">Enter your domain to see DNS records.</p>
                ) : (
                  <div className="space-y-4">
                    {dnsRecords.map((record) => (
                      <DnsRecordCard key={record.id} record={record} />
                    ))}
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
                  <p className="text-xs text-slate-500">DNS changes can take up to 24 hours to propagate.</p>
                  <button
                    type="button"
                    onClick={handleVerifyDomain}
                    disabled={verifying || saving || !customDomain.trim()}
                    className="ui-btn-primary w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {verifying ? "Verifying DNS…" : "Verify domain"}
                  </button>
                </div>
              </SectionCard>
            </>
          )}
        </div>

        {hostingMode === "custom" && (
          <aside className="space-y-6 xl:sticky xl:top-[11rem] xl:self-start">
            <SectionCard icon="ri-list-check-2" title="Setup progress" description="Complete each step to activate your custom domain.">
              <ol className="space-y-4">
                <ChecklistItem step={1} title="Add CNAME record" description="Routes traffic to SentroDesk." done={checklistDone.cname} active={Boolean(customDomain.trim()) && !checklistDone.cname} />
                <ChecklistItem step={2} title="Add TXT verification" description="Confirms domain ownership." done={checklistDone.txt} active={checklistDone.cname && !checklistDone.txt} />
                <ChecklistItem step={3} title="Verify domain & SSL" description="Validates DNS and provisions HTTPS." done={checklistDone.verify} active={checklistDone.txt && !checklistDone.verify} />
                <ChecklistItem step={4} title="Publish docs site" description="Make documentation publicly accessible." done={checklistDone.publish} active={checklistDone.verify && !checklistDone.publish} />
              </ol>
            </SectionCard>
          </aside>
        )}
      </div>
    </div>
  );
}
